import { useCallback, useState } from 'react'
import { isEmpty } from 'lodash'
import { Formik, Field, Form, FormikErrors } from 'formik'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import Loader from 'components/Loader'
import TextBoxReset from 'components/TextBoxReset/TextBoxReset'

import { useCreatePosAccountMutation } from 'store/stadiumApi/posAccounts'
import { useCreatePosAccountRoleMutation } from 'store/stadiumApi/employeeRoles'
import { useGetVenuesQuery } from 'store/porticoApi/venues'
import { getCurrentOrgName } from 'store/appStateSlice/selectors'
import { TAddPosAccountFormValues, TCreatePosAccount } from 'types/posAccounts'
import { randomNumbersGenerator } from 'utils/general/randomNumbersGenerator'
import { getValidEmployeeId } from 'utils/posAccounts/getValidEmployeeId'

type TProps = {
  onClose: () => void
}

const AddPosAccount = ({ onClose }: TProps) => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { isFetching: isFetchingVenues } = useGetVenuesQuery()

  const [createPosAccount] = useCreatePosAccountMutation()
  const [createAccountRoles] = useCreatePosAccountRoleMutation()

  const navigate = useNavigate()
  const orgName = useSelector(getCurrentOrgName)

  const isFetching = isFetchingVenues

  const initialValues: TAddPosAccountFormValues = {
    name: '',
    phone: '',
    email: '',
    pinNumber: '',
    employeeId: '',
    role: 'cashier',
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    pinNumber: Yup.string().required('Required').length(5),
  })

  const disableUpdateButton = useCallback(
    (dirty: boolean, errors: FormikErrors<TAddPosAccountFormValues>) => {
      const disableButton = !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  const onSubmit = (values: TAddPosAccountFormValues) => {
    const { name, phone, email, pinNumber, employeeId, role } = values

    const createAccountValues: TCreatePosAccount = {
      name,
      phone,
      email,
      pinNumber,
      employeeId: getValidEmployeeId(employeeId, orgName, name),
      role,
    }

    createPosAccount(createAccountValues)
      .unwrap()
      .then((data) => {
        createAccountRoles({
          employee_id: data.attendants[0].id,
          role: role,
          type: role === 'manager' ? 'ManagerEmployeeRole' : 'AttendantEmployeeRole',
        })
          .unwrap()
          .then(() => {
            navigate('/pos-accounts')
            toast.success('Success!', {
              position: toast.POSITION.BOTTOM_CENTER,
            })
          })
      })
  }

  return (
    <Modal title="Add POS Account" onClose={onClose} className="-narrow">
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form className="item-settings">
              {isFetching ? (
                <Loader />
              ) : (
                <>
                  <section className="item-settings__section ">
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="Name"
                        name="name"
                        wrapperClassName="-space-right"
                        showValidationErrors={showValidationErrors}
                        onChange={(value: string) => setFieldValue('name', value)}
                        value={values?.name}
                        error={errors?.name}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="Email Address"
                        name="email"
                        showValidationErrors={showValidationErrors}
                        onChange={(value: string) => setFieldValue('email', value)}
                        value={values?.email}
                        error={errors?.email}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="Phone Number"
                        name="phone"
                        onChange={(value: string) => setFieldValue('phone', value)}
                        value={values?.phone}
                        error={errors?.phone}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="External ID"
                        name="employeeId"
                        onChange={(value: string) => setFieldValue('employeeId', value)}
                        value={values?.employeeId}
                        error={errors?.employeeId}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBoxReset}
                        label="PIN Code"
                        name="pinNumber"
                        onChange={(value: string) => setFieldValue('pinNumber', value)}
                        onClick={() =>
                          setFieldValue('pinNumber', String(randomNumbersGenerator(5)))
                        }
                        value={values?.pinNumber}
                        error={errors?.pinNumber}
                        showValidationErrors={showValidationErrors}
                      />
                    </div>
                    <div className="item-settings__border-break"></div>
                  </section>
                  <section className="item-settings__section ">
                    <h3 className="header-title">Account Type</h3>
                    <div role="group">
                      <div className="radio-button-outer">
                        <div className="radio-button-inner">
                          <Field type="radio" name="role" value="cart_attendant" />
                        </div>
                        <div className="radio-button-inner">
                          <div className="radio-button-inner__wrapper">
                            <div className="radio-button-inner__title">Cashier</div>
                            <div className="radio-button-inner__description">
                              Unable to void/refund.
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="item-settings__border-break"></div>
                      <div className="radio-button-outer">
                        <div className="radio-button-inner">
                          <Field type="radio" name="role" value="manager" />
                        </div>
                        <div className="radio-button-inner">
                          <div className="radio-button-inner__wrapper">
                            <div className="radio-button-inner__title">Manager</div>
                            <div className="radio-button-inner__description">
                              Voiding and Refund capabilities. This user is able to approve any POS
                              changes that require a Manager PIN.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                  <div className="btn-wrapper">
                    <button
                      className="btn -update"
                      onClick={onClickSaveButton}
                      disabled={disableUpdateButton(dirty, errors)}
                      type="submit"
                    >
                      Save
                    </button>
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default AddPosAccount
